import React, { Component } from 'react'
import PropTypes from 'prop-types'
import app from '../../../feathers'
import { injectState, provideState, hardUpdate } from 'freactal'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import SortableTree from 'react-sortable-tree'
import Button from '../../../components/Button'
import { IconButton } from 'react-mdl'
import SectionNameField from '../../components/SectionNameField'
import BlockingSpinner from '../../components/BlockingSpinner'
import { sectionDisplayIndex } from '../../../lib'

const Container = styled.div`
  height: 70vh;
  display: flex;
  flex-direction: column;
`
const ActionContainer = styled.div`flex-grow: 0;`
const TitleContainer = styled.div`
  display: flex;
  padding: 5px 5px 0 0;
`
const SectionIndex = styled.p`
  font-weight: bold;
  margin: 0 10px 0 0;
`
const withState = provideState({
  initialState: () => ({
    locked: false,
  }),
  effects: {
    lock: hardUpdate({ locked: true }),
    unlock: hardUpdate({ locked: false }),
  },
})

class UnitContent extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  }
  state = {
    expandedSectionIds: [],
    treeData: this.treeDataFromSections(this.props.unit.sections),
  }
  componentWillReceiveProps(nextProps) {
    const { sections } = nextProps.unit
    if (sections !== this.props.unit.sections) {
      this.setState({
        treeData: this.treeDataFromSections(sections),
      })
    }
  }
  sectionToNode({ treeData, sectionIndex, section }) {
    return {
      expanded: treeData
        ? this.state.expandedSectionIds.includes(section._id)
        : false,
      section,
      sectionIndex,
      title: () => (
        <TitleContainer>
          {sectionIndex.length > 1 && (
            <SectionIndex>{sectionDisplayIndex(sectionIndex)}</SectionIndex>
          )}
          <SectionNameField
            name={section.name}
            unitId={this.props.unit._id}
            sectionIndex={sectionIndex}
            style={{ padding: 0 }}
          />
        </TitleContainer>
      ),
      children:
        section.subsections &&
        this.treeDataFromSections(section.subsections, sectionIndex),
    }
  }
  treeDataFromSections(sections, parentIndex = []) {
    const treeData = this.state && this.state.treeData
    return sections.map((section, index) => {
      const sectionIndex = [...parentIndex, index]
      return this.sectionToNode({ treeData, sectionIndex, section })
    })
  }
  sectionsFromTreeData(treeData) {
    return treeData.map(({ section, children }) => {
      return {
        ...section,
        subsections:
          (children && children.length) > 0
            ? this.sectionsFromTreeData(children)
            : undefined,
      }
    })
  }
  handleChangeTreeData = treeData => {
    // syncronizing the treedata state client-side
    this.setState({ treeData })
  }
  handleToggleSection = ({ node: { section }, expanded }) => {
    if (expanded) {
      this.setState(({ expandedSectionIds: ids }) => ({
        expandedSectionIds: [...ids, section._id],
      }))
    } else {
      this.setState(({ expandedSectionIds: ids }) => ({
        expandedSectionIds: ids.filter(id => id !== section._id),
      }))
    }
  }
  // handle movements of sections
  handleMoveNode = async ({ treeData }) => {
    await this.props.effects.lock()
    const { _id: unitId } = this.props.state.unit
    const sections = this.sectionsFromTreeData(treeData)
    await app.service('units').patch(unitId, {
      sections,
    })
    await this.props.effects.unlock()
  }
  addSection = async sectionIndex => {
    const { state: { unit } } = this.props
    // sectionIndex ? create a child section : create a top level section
    const sectionPath = sectionIndex
      ? `.${sectionIndex.join('.subsections.')}.subsections`
      : ''
    app.service('units').patch(unit._id, {
      $push: {
        [`sections${sectionPath}`]: {
          name: 'New Section',
        },
      },
    })
  }
  deleteSection = async _id => {
    const { state: { unit } } = this.props
    if (!_id || !unit._id) return
    app.service('units').patch(unit._id, {
      $pull: {
        sections: {
          _id,
        },
      },
    })
  }
  render() {
    const { treeData } = this.state
    const { state: { locked, unit: { _id: unitId } } } = this.props
    return (
      <Container>
        <ActionContainer>
          <Button raised primary onClick={() => this.addSection()}>
            Add Section
          </Button>
        </ActionContainer>
        <SortableTree
          maxDepth={4}
          treeData={treeData}
          onChange={this.handleChangeTreeData}
          onVisibilityToggle={this.handleToggleSection}
          onMoveNode={this.handleMoveNode}
          generateNodeProps={({ path, node }) => ({
            buttons: [
              // display add section button on first to third-level sections
              path.length !== 4 && (
                <IconButton
                  colored
                  name="add"
                  onClick={() => this.addSection(node.sectionIndex)}
                  key={`add${path}`}
                />
              ),
              // display delete button for first-level sections
              path.length === 1
                ? [
                    <IconButton
                      colored
                      onClick={() => this.deleteSection(node.section._id)}
                      disabled={
                        node.section.subsections &&
                        node.section.subsections.length > 0
                      }
                      name="delete"
                      style={{
                        verticalAlign: 'middle',
                      }}
                      key={`delete${path}`}
                    />,
                  ]
                : path.length === 2
                  ? // display link to edit content for second-level sections
                    [
                      <Link
                        to={`./${node.sectionIndex.join('.')}/`}
                        key={`editlink${path}`}>
                        <IconButton
                          colored
                          name="edit"
                          style={{
                            verticalAlign: 'middle',
                          }}
                          key={`edit${path}`}
                        />
                      </Link>,
                    ]
                  : [
                      // display add mapping button to third and fourth level sections
                      <Link
                        to={`/units/${unitId}/learner-guide/section-mapping/${node.sectionIndex.join(
                          '.',
                        )}/`}
                        key={`mappinglink${path}`}>
                        <IconButton
                          colored
                          accent
                          name="map"
                          key={`mapping${path}`}
                        />
                      </Link>,
                    ],
            ],
          })}
        />
        {locked && <BlockingSpinner />}
      </Container>
    )
  }
}

export default withState(injectState(UnitContent))



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/LearnerGuide/UnitContent.js